import { doesTemplateRequireQuestionnaire } from "./templateQuestionnaireHelpers";
import { messageSendService } from "../services/messageSendService";
import type {
  MessagingQuestionnaireOption,
  SendManyWhatsAppMessageInput,
  SendMessageResult,
  SendWhatsAppMessageInput,
} from "../types";

type SendParams = Omit<SendWhatsAppMessageInput, "wabaId" | "phoneNumberId"> & {
  wabaId?: string | null;
  phoneNumberId?: string | null;
};

function isQuestionnaireValid(params: {
  templateRequiresQuestionnaire: boolean;
  questionnaire?: MessagingQuestionnaireOption | null;
}): { valid: boolean; errorMessage?: string } {
  if (!params.templateRequiresQuestionnaire) return { valid: true };
  if (params.questionnaire?.id) return { valid: true };
  return {
    valid: false,
    errorMessage: "Selecione um questionário para enviar esta mensagem.",
  };
}

/**
 * Validações + envio unitário (usado pelo hook e testável isoladamente).
 */
export async function runSendWhatsAppMessageOrchestrator(
  sendParams: SendParams,
  context: { wabaIdRank1: string | null; phoneNumberIdRank1: string | null },
): Promise<SendMessageResult> {
  const resolvedPhoneNumberId =
    (sendParams.phoneNumberId ?? context.phoneNumberIdRank1 ?? "").trim() ||
    null;
  const resolvedWabaId =
    (sendParams.wabaId ?? context.wabaIdRank1 ?? "").trim() || null;

  if (!sendParams.patient?.id) {
    return {
      status: "error",
      errorMessage: "Selecione um destinatário para enviar a mensagem.",
    };
  }

  if (!sendParams.template?.id) {
    return {
      status: "error",
      errorMessage: "Selecione um template para enviar a mensagem.",
    };
  }

  if (!sendParams.appointment?.id && sendParams.appointment?.id !== 0) {
    return {
      status: "error",
      errorMessage: "Appointment inválido ou ausente.",
    };
  }

  const templateRequiresQuestionnaire = doesTemplateRequireQuestionnaire(
    sendParams.template,
  );
  const questionnaireValidation = isQuestionnaireValid({
    templateRequiresQuestionnaire,
    questionnaire: sendParams.questionnaire,
  });
  if (!questionnaireValidation.valid) {
    return {
      status: "error",
      errorMessage:
        questionnaireValidation.errorMessage ?? "Questionário inválido.",
    };
  }

  return messageSendService.send({
    patient: sendParams.patient,
    appointment: sendParams.appointment,
    template: sendParams.template,
    questionnaire: sendParams.questionnaire ?? null,
    phoneNumberId: resolvedPhoneNumberId,
    wabaId: resolvedWabaId,
    requesterReference: sendParams.requesterReference ?? null,
    automationConfig: sendParams.automationConfig,
  });
}

/**
 * Envio para vários destinatários:
 * - 1 destinatário → fluxo unitário (`send`), sem regressão.
 * - 2+ destinatários → um único POST com `for[]` agregado (`sendBatch`).
 */
export async function runSendManyWhatsAppMessagesOrchestrator(
  params: SendManyWhatsAppMessageInput,
  context: { wabaIdRank1: string | null; phoneNumberIdRank1: string | null },
): Promise<SendMessageResult> {
  if (params.patients.length === 0) {
    return {
      status: "error",
      errorMessage: "Nenhum destinatário selecionado.",
    };
  }

  const resolvedPhoneNumberId =
    (params.phoneNumberId ?? context.phoneNumberIdRank1 ?? "").trim() || null;
  const resolvedWabaId =
    (params.wabaId ?? context.wabaIdRank1 ?? "").trim() || null;

  const { patients, ...shared } = params;

  // Unitário: preserva o caminho atual (validações + POST com um item em `for`).
  if (patients.length === 1) {
    return runSendWhatsAppMessageOrchestrator(
      {
        ...shared,
        patient: patients[0],
        phoneNumberId: resolvedPhoneNumberId,
        wabaId: resolvedWabaId,
      },
      context,
    );
  }

  if (!shared.template?.id) {
    return {
      status: "error",
      errorMessage: "Selecione um template para enviar a mensagem.",
    };
  }

  if (!shared.appointment?.id && shared.appointment?.id !== 0) {
    return {
      status: "error",
      errorMessage: "Appointment inválido ou ausente.",
    };
  }

  const templateRequiresQuestionnaire = doesTemplateRequireQuestionnaire(
    shared.template,
  );
  const questionnaireValidation = isQuestionnaireValid({
    templateRequiresQuestionnaire,
    questionnaire: shared.questionnaire,
  });
  if (!questionnaireValidation.valid) {
    return {
      status: "error",
      errorMessage:
        questionnaireValidation.errorMessage ?? "Questionário inválido.",
    };
  }

  return messageSendService.sendBatch({
    patients,
    appointment: shared.appointment,
    template: shared.template,
    questionnaire: shared.questionnaire ?? null,
    phoneNumberId: resolvedPhoneNumberId,
    wabaId: resolvedWabaId,
    requesterReference: shared.requesterReference ?? null,
    automationConfig: shared.automationConfig,
  });
}
